import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { animate, remove } from 'animejs';

const NotificationContext = createContext({ notify: () => {} });

function Toast({ toast, onRemove }) {
  const ref = useRef(null);
  const duration = toast.duration || 3000;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    animate(el, {
      translateX: [32, 0],
      opacity: [0, 1],
      duration: 320,
      easing: 'easeOutQuad',
    });

    const timer = setTimeout(() => {
      animate(el, {
        translateX: [0, 28],
        opacity: [1, 0],
        duration: 220,
        easing: 'easeInQuad',
        complete: () => onRemove(toast.id),
      });
    }, duration);

    return () => {
      clearTimeout(timer);
      remove(el);
    };
  }, [toast.id, duration, onRemove]);

  const handleClose = useCallback(() => {
    const el = ref.current;
    if (!el) { onRemove(toast.id); return; }
    animate(el, {
      translateX: [0, 28],
      opacity: [1, 0],
      duration: 180,
      easing: 'easeInQuad',
      complete: () => onRemove(toast.id),
    });
  }, [onRemove, toast.id]);

  const tone = toast.level || 'info';
  const accent = {
    success: 'var(--success)',
    warning: '#f59e0b',
    error: 'var(--danger)',
    info: 'var(--accent)',
  }[tone] || 'var(--accent)';

  return (
    <div
      ref={ref}
      className="toast-card"
      style={{
        background: 'color-mix(in srgb, var(--panel) 92%, rgba(0,0,0,0.4))',
        border: `1px solid color-mix(in srgb, ${accent} 50%, var(--border))`,
        boxShadow: '0 14px 42px rgba(0,0,0,0.28)',
        borderRadius: 14,
        padding: '12px 14px',
        color: 'var(--text)',
        minWidth: 260,
        maxWidth: 340,
        display: 'grid',
        gap: 6,
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 10, height: 10, borderRadius: 999, background: accent, boxShadow: `0 0 10px color-mix(in srgb, ${accent} 60%, transparent)` }} />
        <div style={{ fontWeight: 700, fontSize: 14 }}>{toast.title || 'Powiadomienie'}</div>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Zamknij powiadomienie"
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--muted)',
            cursor: 'pointer',
            marginLeft: 'auto',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      {toast.message ? <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--muted)' }}>{toast.message}</div> : null}
    </div>
  );
}

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [authToken, setAuthToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  });
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(1000);
  const shouldReconnectRef = useRef(true);

  const syncToken = useCallback(() => {
    if (typeof window === 'undefined') return;
    const next = localStorage.getItem('token') || sessionStorage.getItem('token');
    setAuthToken((prev) => (prev === next ? prev : next));
  }, []);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((payload) => {
    const id = payload?.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const next = {
      id,
      title: payload?.title || 'Powiadomienie',
      message: payload?.message || '',
      level: payload?.level || 'info',
      duration: payload?.duration || 3000,
    };
    setToasts((prev) => [...prev, next].slice(-4));
    return id;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleStorage = (e) => {
      if (!e || !e.key) {
        syncToken();
        return;
      }
      if (e.key === 'token') syncToken();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncToken();
    };

    const intervalId = window.setInterval(syncToken, 15000);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', syncToken);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', syncToken);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [syncToken]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Reset reconnection intent on each effect run
    shouldReconnectRef.current = true;

    const closeSocket = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (err) { /* ignore */ }
        wsRef.current = null;
      }
    };

    if (!authToken) {
      shouldReconnectRef.current = false;
      closeSocket();
      return undefined;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      shouldReconnectRef.current = true;
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${protocol}://${window.location.host}/api/notifications/ws?token=${encodeURIComponent(authToken)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = 1000;
      };

      ws.onmessage = (event) => {
        try {
          const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (!payload) return;
          if (payload.type === 'info' && payload.code === 'notifications_disabled') {
            shouldReconnectRef.current = false;
            notify({
              id: 'notifications-disabled',
              title: 'Powiadomienia',
              message: payload.message || 'Powiadomienia są wyłączone w tym środowisku.',
              level: 'info',
              duration: 6000,
            });
            try { ws.close(); } catch (err) { /* ignore */ }
            return;
          }
          const text = `${payload.title || ''} ${payload.body || payload.message || ''}`.toLowerCase();
          if (text.includes('zablokowan')) {
            window.dispatchEvent(new CustomEvent('user:suspended', { detail: payload }));
          }
          // Fan-out so other components (badges, lists) can react immediately.
          window.dispatchEvent(new CustomEvent('notifications:new', { detail: payload }));
          notify({
            id: payload.id,
            title: payload.title || 'Powiadomienie',
            message: payload.body || payload.message || '',
            level: payload.level || 'info',
            duration: 5000,
          });
        } catch (err) {
          // Ignore malformed payloads
        }
      };

      const scheduleReconnect = () => {
        if (cancelled || !shouldReconnectRef.current) return;
        const delay = Math.min(backoffRef.current, 15000);
        reconnectTimerRef.current = setTimeout(() => {
          backoffRef.current = Math.min(backoffRef.current * 2, 15000);
          connect();
        }, delay);
      };

      ws.onclose = () => {
        if (cancelled || !shouldReconnectRef.current) return;
        scheduleReconnect();
      };

      ws.onerror = () => {
        try { ws.close(); } catch (err) { /* ignore */ }
      };
    };

    connect();

    return () => {
      cancelled = true;
      closeSocket();
    };
  }, [authToken, notify]);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div
        className="toast-stack"
        style={{
          position: 'fixed',
          right: 18,
          bottom: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => (
          <div key={toast.id} style={{ pointerEvents: 'auto' }}>
            <Toast toast={toast} onRemove={remove} />
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  return useContext(NotificationContext);
}
